/**
 * Lender fees for advertised APRs, taken from the same place every estimate
 * in the app takes them: each program's closing-cost calculator, merged with
 * the admin's published fee overrides (computeMergedFeeBreakdown). Only fees
 * marked "included in APR" count.
 *
 * Government fees (FHA mortgage insurance, VA funding fee, USDA guarantee
 * fee) are left out here; advertisedApr applies them from the published
 * percentages, including the monthly portions. Prepaid interest is returned
 * as days so the APR applies it at the advertised rate.
 */

import { USER_LOAN_PROGRAM } from '@/config/constants';
import type {
  CalculatedFeeRule,
  FeeItem,
} from '@/apis/extended/admin/interestRateApi';
import {
  REPRESENTATIVE_LOAN_AMOUNT,
  type PublishedAprAssumptions,
} from '@/utils/advertisedApr';
import { calculateClosingDisclosure } from '@/utils/calculator';
import {
  FHA_UPFRONT_MIP_PERCENT,
  fhaAnnualMipRate,
} from '@/utils/calculator/assumptions';
import {
  computeMergedFeeBreakdown,
  type CalculatedFeeRule as EngineFeeRule,
} from '@/utils/fees/feeEngine';

/** Published program key → calculator program. */
const PROGRAM_BY_KEY: Record<string, USER_LOAN_PROGRAM> = {
  conventional: USER_LOAN_PROGRAM.CONVENTIONAL,
  fha: USER_LOAN_PROGRAM.FHA,
  va: USER_LOAN_PROGRAM.VA,
  usda: USER_LOAN_PROGRAM.USDA,
  dscr: USER_LOAN_PROGRAM.DSCR,
  commercial: USER_LOAN_PROGRAM.COMMERCIAL,
  fixAndFlip: USER_LOAN_PROGRAM.FIX_AND_FLIP,
  bridge: USER_LOAN_PROGRAM.BRIDGE,
  heloc: USER_LOAN_PROGRAM.HELOC,
  heloan: USER_LOAN_PROGRAM.HELOAN,
  secondMortgage: USER_LOAN_PROGRAM.SECOND_MORTGAGE,
};

/** Applied from published percentages by advertisedApr instead. */
const GOVERNMENT_FEE_KEYS = new Set([
  'calc_upfrontMIPfee',
  'calc_upfrontGuaranteeFee',
  'calc_vaFundingFee',
]);
const PREPAID_INTEREST_KEY = 'calc_prepaidInterest';
/** Days of prepaid interest every calculator charges. */
const PREPAID_INTEREST_DAYS = 15;

/** 20% down on the representative loan amount. */
const REPRESENTATIVE_PRICE = REPRESENTATIVE_LOAN_AMOUNT / 0.8;

export interface AdvertisedFees {
  /** APR-included lender and third-party fees, in dollars. */
  lenderFees: number;
  prepaidInterestDays: number;
  /**
   * Government fee percentages for the representative loan, from the same
   * rules the calculators use (FHA MIP schedule, VA funding fee by down
   * payment, USDA guarantee fees). Overrides the published percentages.
   */
  government: Partial<PublishedAprAssumptions>;
}

const percentOfLoan = (amount: unknown) => {
  const n = Number(amount);
  return Number.isFinite(n) && n > 0
    ? Math.round((n / REPRESENTATIVE_LOAN_AMOUNT) * 100 * 1000) / 1000
    : 0;
};

function governmentPercents(
  type: USER_LOAN_PROGRAM,
  estimation: Record<string, unknown>
): Partial<PublishedAprAssumptions> {
  switch (type) {
    case USER_LOAN_PROGRAM.FHA:
      return {
        fhaUfmipPercent: FHA_UPFRONT_MIP_PERCENT,
        fhaAnnualMIPPercent:
          fhaAnnualMipRate(REPRESENTATIVE_LOAN_AMOUNT, 80) * 100,
      };
    case USER_LOAN_PROGRAM.VA:
      return {
        vaFundingFeePercent: percentOfLoan(estimation.upfrontFundingFee),
      };
    case USER_LOAN_PROGRAM.USDA:
      return {
        usdaUpfrontGuaranteeFeePercent: percentOfLoan(
          estimation.upfrontGuaranteeFee
        ),
        usdaAnnualFeePercent: percentOfLoan(
          Number(estimation.monthlyGuaranteeFee) * 12
        ),
      };
    default:
      return {};
  }
}

export function advertisedFeesFor(
  programKey: string,
  ratePercent: number,
  published: {
    feeItems?: FeeItem[];
    calculatedFeeRules?: CalculatedFeeRule[];
  } = {}
): AdvertisedFees | null {
  const type = PROGRAM_BY_KEY[programKey];
  if (!type || !Number.isFinite(ratePercent)) return null;
  try {
    const estimation = calculateClosingDisclosure({
      type,
      listPrice: REPRESENTATIVE_PRICE,
      offerPrice: REPRESENTATIVE_PRICE,
      downPayment: REPRESENTATIVE_PRICE - REPRESENTATIVE_LOAN_AMOUNT,
      marketInterestRate: ratePercent,
    });
    const breakdown = computeMergedFeeBreakdown({
      estimation,
      feeItems: published.feeItems,
      // Published rules carry plain-string keys; unknown keys never match.
      calculatedFeeRules: published.calculatedFeeRules as
        | EngineFeeRule[]
        | undefined,
      loanAmount: REPRESENTATIVE_LOAN_AMOUNT,
    });
    let lenderFees = 0;
    let prepaidInterestDays = 0;
    for (const fee of Object.values(breakdown.byCategory).flat()) {
      if (!fee.includedInApr || GOVERNMENT_FEE_KEYS.has(fee.key)) continue;
      if (fee.key === PREPAID_INTEREST_KEY) {
        prepaidInterestDays = PREPAID_INTEREST_DAYS;
        continue;
      }
      lenderFees += Number(fee.computedAmount) || 0;
    }
    return {
      lenderFees: Math.round(lenderFees),
      prepaidInterestDays,
      government: governmentPercents(
        type,
        estimation as unknown as Record<string, unknown>
      ),
    };
  } catch {
    return null;
  }
}
